
import * as faceapi from "face-api.js";
import { useEffect, useRef, useState, useCallback } from "react";

const FaceDetection = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const detectionIntervalRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  
  // Load models
  useEffect(() => {
    loadModels();
    
    return () => {
      stopDetection();
      stopCamera();
    };
  }, []);

  const loadModels = async () => {
    try {
      setLoading(true);
      const MODEL_URL = "/models";

      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        faceapi.nets.faceExpressionNet.loadFromUri(MODEL_URL),
        faceapi.nets.ageGenderNet.loadFromUri(MODEL_URL),
      ]);

      setLoading(false);
      setError(null);
    } catch (err) {
      console.error('Error loading models:', err);
      setError(`Failed to load models: ${err.message || 'Unknown error'}`);
      setLoading(false);
    }
  };

  // Check camera permissions
  const checkCameraPermissions = async () => {
    try {
      const permissionStatus = await navigator.permissions.query({ name: 'camera' });
      return permissionStatus.state;
    } catch (err) {
      console.warn('Could not check camera permissions:', err);
      return 'prompt'; // Default to prompt if we can't check
    }
  };

  // Request camera access
  const startVideo = async () => {
    try {
      // Check if camera permission is already granted
      const permissionState = await checkCameraPermissions();
      
      if (permissionState === 'denied') {
        setError('Camera permission has been denied. Please enable camera access in your browser settings.');
        setLoading(false);
        return;
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 720 }, 
          height: { ideal: 560 } 
        } 
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraActive(true);
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      
      if (err.name === 'NotAllowedError') {
        setError('Camera access denied. Please allow camera permission to use this feature.');
      } else if (err.name === 'NotFoundError' || err.name === 'OverconstrainedError') {
        setError('No camera found or camera constraints could not be satisfied.');
      } else if (err.name === 'NotReadableError') {
        setError('Camera is being used by another application.');
      } else {
        setError(`Camera access failed: ${err.message || 'Unknown error'}`);
      }
      
      setLoading(false);
    }
  };

  // Stop camera stream
  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setCameraActive(false);
    }
  };

  // Stop detection interval
  const stopDetection = () => {
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }
  };

  // Handle video play and start detection
  const handleVideoPlay = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    // Clear any existing interval
    stopDetection();
    
    // Start detection interval with optimized settings
    detectionIntervalRef.current = setInterval(async () => {
      try {
        // Only run detection if the video is actually playing
        if (!videoRef.current || videoRef.current.paused || videoRef.current.ended) {
          return;
        }
        
        const detections = await faceapi
          .detectAllFaces(
            videoRef.current,
            new faceapi.TinyFaceDetectorOptions({
              inputSize: 320, // Smaller input size for better performance
              scoreThreshold: 0.5 // Adjust threshold for performance
            })
          )
          .withFaceLandmarks()
          .withFaceExpressions()
          .withAgeAndGender();

        const canvas = canvasRef.current;
        const displaySize = {
          width: videoRef.current.videoWidth,
          height: videoRef.current.videoHeight,
        };

        if (canvas) {
          faceapi.matchDimensions(canvas, displaySize);
          const resizedDetections = faceapi.resizeResults(detections, displaySize);

          const ctx = canvas.getContext("2d");
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          // Draw hacker-style detections
          // Using direct canvas drawing instead of DrawOptions which doesn't exist in face-api.js
          
          // Draw hacker-style boxes around faces
          resizedDetections.forEach(detection => {
            const box = detection.detection.box;
            
            // Draw pulsating rectangle
            ctx.strokeStyle = '#00ff00';
            ctx.lineWidth = 2;
            ctx.shadowColor = '#00ff00';
            ctx.shadowBlur = 10;
            ctx.strokeRect(box.x, box.y, box.width, box.height);
            
            // Draw grid inside the box
            ctx.strokeStyle = 'rgba(0, 255, 0, 0.3)';
            ctx.lineWidth = 1;
            const step = Math.min(box.width, box.height) / 4;
            for (let i = 1; i < 4; i++) {
              // Vertical lines
              ctx.beginPath();
              ctx.moveTo(box.x + i * step, box.y);
              ctx.lineTo(box.x + i * step, box.y + box.height);
              ctx.stroke();
              
              // Horizontal lines
              ctx.beginPath();
              ctx.moveTo(box.x, box.y + i * step);
              ctx.lineTo(box.x + box.width, box.y + i * step);
              ctx.stroke();
            }
          });
          
          // Draw hacker-style landmarks
          if (resizedDetections.length > 0) {
            resizedDetections.forEach(detection => {
              if (detection.landmarks) {
                const parts = detection.landmarks._positions;
                
                // Draw connection lines between landmarks
                ctx.strokeStyle = 'rgba(0, 255, 0, 0.4)';
                ctx.lineWidth = 1;
                
                // Draw landmark points
                ctx.fillStyle = '#00ff00';
                parts.forEach(point => {
                  ctx.beginPath();
                  ctx.arc(point.x, point.y, 1, 0, 2 * Math.PI);
                  ctx.fill();
                });
              }
            });
          }
          
          // Draw hacker-style expressions
          resizedDetections.forEach(detection => {
            if (detection.expressions) {
              // Draw expression as small dots around face
              const box = detection.detection.box;
              const centerX = box.x + box.width / 2;
              const centerY = box.y + box.height / 2;
              
              // Draw small circles representing expression confidence
              Object.entries(detection.expressions).slice(0, 5).forEach(([expression, confidence], idx) => {
                const angle = (idx / 5) * 2 * Math.PI;
                const radius = 20 + confidence * 10;
                const dotX = centerX + Math.cos(angle) * radius;
                const dotY = centerY + Math.sin(angle) * radius;
                
                ctx.fillStyle = `rgba(0, 255, 0, ${confidence})`;
                ctx.beginPath();
                ctx.arc(dotX, dotY, 2, 0, 2 * Math.PI);
                ctx.fill();
              });
            }
          });

          // Display age and gender in modern label
          resizedDetections.forEach((det) => {
            const { age, gender, detection, expressions } = det;
            const box = detection.box;
            
            // Get the dominant emotion
            const dominantExpression = Object.keys(expressions).reduce((a, b) => 
              expressions[a] > expressions[b] ? a : b
            );
            
            // Create hacker styled label
            const label = `[AGE: ${Math.round(age)}y] [GENDER: ${gender.toUpperCase()}] [EMOTION: ${dominantExpression.toUpperCase()}]`;
            
            // Draw hacker styled label
            ctx.font = '12px "Courier New", monospace';
            ctx.fillStyle = '#00ff00';
            ctx.textAlign = 'left';
            
            // Draw background box
            const textWidth = ctx.measureText(label).width;
            ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
            ctx.fillRect(box.left, box.top - 20, textWidth + 10, 20);
            
            // Draw border
            ctx.strokeStyle = '#00ff00';
            ctx.lineWidth = 1;
            ctx.strokeRect(box.left, box.top - 20, textWidth + 10, 20);
            
            // Draw text
            ctx.fillStyle = '#00ff00';
            ctx.shadowColor = '#00ff00';
            ctx.shadowBlur = 10;
            ctx.fillText(label, box.left + 5, box.top - 5);
          });
        }
      } catch (err) {
        console.error('Detection error:', err);
      }
    }, 150); // Optimized interval for better performance
  }, []);

  // Toggle camera
  const toggleCamera = async () => {
    if (cameraActive) {
      stopDetection();
      stopCamera();
    } else {
      await startVideo();
    }
  };

  if (error) {
    return (
      <div className="face-detection-container matrix-bg">
        <h2>[ DARKFORCE CORP ] SECURITY BREACH PROTOCOL</h2>
        <div className="error-message">
          <p>&gt; DFC-SYSTEM ALERT: {error}</p>
          <div className="error-operator">OPERATOR: AMIR ALI | DARKFORCE CORP</div>
          <button onClick={() => window.location.reload()} className="retry-button">
            &gt; RESTART DFC-SYSTEM
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="face-detection-container matrix-bg">
        <h2>[ DARKFORCE CORP ] SYSTEM INITIALIZATION</h2>
        <div className="loading-state">
          <p>&gt;&gt; DFC-BIOMETRIC-SYS LOADING...</p>
          <div className="spinner"></div>
          <p>&gt; DARKFORCE NEURAL NETWORKS: ACTIVATED</p>
          <div className="loading-operator">OPERATOR: AMIR ALI | DARKFORCE CORP</div>
        </div>
      </div>
    );
  }

  return (
    <div className="face-detection-container matrix-bg">
      <h2>[ DARKFORCE CORP ] BIOMETRIC IDENTIFICATION SYSTEM</h2>
      <div className="company-tag">SECURITY LEVEL: ULTRA | OPERATOR: AMIR ALI | DFC-2026</div>
      
      <div className="controls">
        <button 
          onClick={toggleCamera}
          className={`camera-toggle ${cameraActive ? 'active' : ''}`}
        >
          {cameraActive ? '> SHUTDOWN DFC-BIOMETRIC-SYS' : '> ACTIVATE DFC-BIOMETRIC-SYS'}
        </button>
      </div>
      
      <div className="video-container">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          width="720"
          height="560"
          onPlay={handleVideoPlay}
          className="video-element"
        />

        <canvas
          ref={canvasRef}
          className="overlay-canvas"
        />
      </div>
      
      <div className="info">
        <p>&gt;&gt; DFC BIOMETRIC SCANNER ONLINE... AUTHENTICATING BIOLOGICAL SIGNATURES</p>
                <div className="operator-status">OPERATOR: AMIR ALI | DARKFORCE CORP | DFC-IDENTITY v2.6</div>
        <div className="scan-line"></div>
      </div>
    </div>
  );
};

export default FaceDetection;

